import type { dxFileManagerDetailsColumn } from 'devextreme/ui/file_manager';
import Button from 'devextreme/ui/button';
import type dxDataGrid from 'devextreme/ui/data_grid';
import { fileExtension } from '../mediaViewer/mediaFileTypes';

export const FILE_BROWSER_ACTIONS_COLUMN = 'actions';
export const FILE_BROWSER_EXTENSION_COLUMN = 'extension';
export const NO_FILE_EXTENSION_LABEL = '—';

type FileItemRow = {
  fileItem?: {
    key?: string;
    name?: string;
    isDirectory?: boolean;
    isParentFolder?: boolean;
  };
};

type DetailColumnWithCalculators = dxFileManagerDetailsColumn & {
  calculateCellValue?: (rowData: FileItemRow) => string;
  calculateSortValue?: (rowData: FileItemRow) => string;
  cellTemplate?: (container: HTMLElement, cellInfo: ActionsCellInfo) => void;
  allowSorting?: boolean;
  allowFiltering?: boolean;
  allowHeaderFiltering?: boolean;
  allowHiding?: boolean;
  minWidth?: number;
};

type ActionsCellInfo = {
  data: FileItemRow;
  component: dxDataGrid;
};

export function displayFileExtension(fileName: string, isDirectory: boolean): string {
  if (isDirectory) return 'Folder';
  const ext = fileExtension(fileName);
  return ext ? ext.toUpperCase() : NO_FILE_EXTENSION_LABEL;
}

function extensionCellValue(rowData: FileItemRow): string {
  const item = rowData.fileItem;
  if (!item || item.isParentFolder) return '';
  return displayFileExtension(item.name ?? '', !!item.isDirectory);
}

function extensionSortValue(rowData: FileItemRow): string {
  const item = rowData.fileItem;
  if (!item || item.isParentFolder) return '';
  if (item.isDirectory) return ' folder';
  const ext = fileExtension(item.name ?? '');
  return ext || ' ~';
}

function openRowContextMenu(
  grid: dxDataGrid,
  rowData: FileItemRow,
  anchor: HTMLElement,
  event?: Event | null
): void {
  const key = rowData.fileItem?.key;
  if (!key) return;

  const rowElement = anchor.closest('.dx-data-row') as HTMLElement | null;
  if (!rowElement) return;

  const revealMenu = () => {
    const rect = anchor.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : rect.left + rect.width / 2;
    const clientY = event instanceof MouseEvent ? event.clientY : rect.bottom;
    rowElement.dispatchEvent(
      new MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX,
        clientY
      })
    );
  };

  const selected = grid.getSelectedRowKeys();
  if (selected.length === 1 && selected[0] === key) {
    revealMenu();
    return;
  }

  void Promise.resolve(grid.selectRows([key], false)).then(revealMenu);
}

function createActionsCellTemplate(container: HTMLElement, cellInfo: ActionsCellInfo): void {
  if (cellInfo.data.fileItem?.isParentFolder) return;

  const host = document.createElement('div');
  host.className = 'jarvis-file-browser-actions-cell';
  container.appendChild(host);

  new Button(host, {
    icon: 'overflow',
    stylingMode: 'text',
    hint: 'Actions',
    onClick: (e) => {
      e.event?.stopPropagation();
      openRowContextMenu(cellInfo.component, cellInfo.data, host, e.event ?? null);
    }
  });
}

function buildActionsColumn(): DetailColumnWithCalculators {
  return {
    dataField: FILE_BROWSER_ACTIONS_COLUMN,
    caption: '',
    width: 44,
    minWidth: 44,
    alignment: 'center',
    allowSorting: false,
    allowFiltering: false,
    allowHeaderFiltering: false,
    allowHiding: true,
    hidingPriority: 1,
    cssClass: 'jarvis-file-browser-actions-column',
    cellTemplate: createActionsCellTemplate
  };
}

function buildExtensionColumn(): DetailColumnWithCalculators {
  return {
    dataField: FILE_BROWSER_EXTENSION_COLUMN,
    caption: 'Type',
    width: 88,
    alignment: 'left',
    allowHiding: true,
    hidingPriority: 2,
    calculateCellValue: extensionCellValue,
    calculateSortValue: extensionSortValue
  };
}

function insertColumnBefore(
  columns: dxFileManagerDetailsColumn[],
  beforeDataField: string,
  column: DetailColumnWithCalculators
): dxFileManagerDetailsColumn[] {
  const index = columns.findIndex((entry) => entry.dataField === beforeDataField);
  const insertAt = index >= 0 ? index : columns.length;
  return [...columns.slice(0, insertAt), column, ...columns.slice(insertAt)];
}

function insertColumnAfter(
  columns: dxFileManagerDetailsColumn[],
  afterDataField: string,
  column: DetailColumnWithCalculators
): dxFileManagerDetailsColumn[] {
  const index = columns.findIndex((entry) => entry.dataField === afterDataField);
  const insertAt = index >= 0 ? index + 1 : columns.length;
  return [...columns.slice(0, insertAt), column, ...columns.slice(insertAt)];
}

export function customizeFileBrowserDetailColumns(
  columns: dxFileManagerDetailsColumn[]
): dxFileManagerDetailsColumn[] {
  let next = [...columns];

  if (!next.some((column) => column.dataField === FILE_BROWSER_EXTENSION_COLUMN)) {
    next = insertColumnAfter(next, 'name', buildExtensionColumn());
  }

  if (!next.some((column) => column.dataField === FILE_BROWSER_ACTIONS_COLUMN)) {
    next = insertColumnBefore(next, FILE_BROWSER_EXTENSION_COLUMN, buildActionsColumn());
  }

  return next;
}
